import { gql } from "@apollo/client";

// Query to get all Salesforce integrations
export const SFDC_INTEGRATIONS_QUERY = gql`
  query SfdcIntegrations {
    integrations {
      id
      name
      environment
      organizationId
      lastSyncFailed
      managedPackageCurrentVersion
      needsResync
      syncSettings {
        autoSync
        syncAccounts
        syncLeads
        exportItSpend
        exportTechInstalls
        exportFirmographics
        exportAccountScoring
        exportCloudDynamics
        selectedProducts
        selectedSpendCategories
        selectedScoringId
        useCountryLevelMatching
        accountsCountryField
        localizeInstalls
        customHgIdMatching
      }
    }
  }
`;

// Query to get latest sync results for an integration
export const GET_LATEST_SYNC_RESULT_QUERY = gql`
  query getLatestSyncResult($integrationId: ID!) {
    latestSyncResult(integrationId: $integrationId) {
      totalRecords
      matchedRecords
      notMatchedRecords
      syncTime
      hasErrors
    }
  }
`;

// Query to get integration synchronization state
export const GET_INTEGRATION_SYNCHRONIZATION_QUERY = gql`
  query getIntegrationSynchronization($integrationId: ID!) {
    enrichmentSynchronization(integrationId: $integrationId) {
      state
    }
  }
`;

// Mutation to create a new integration
export const CREATE_INTEGRATION_MUTATION = gql`
  mutation createIntegration(
    $authorizationCodeGrant: String!
    $redirectUri: String!
    $name: String!
    $createdByEmail: String!
    $environment: String!
    $autoSync: Boolean!
    $syncAccounts: Boolean!
    $syncLeads: Boolean!
    $exportTechInstalls: Boolean!
    $exportItSpend: Boolean!
    $exportFirmographics: Boolean!
    $exportAccountScoring: Boolean!
    $exportCloudDynamics: Boolean!
    $useCountryLevelMatching: Boolean!
    $accountsCountryField: String
    $localizeInstalls: Boolean!
    $customHgIdMatching: Boolean!
  ) {
    createIntegration(
      input: {
        authorizationCodeGrant: $authorizationCodeGrant
        name: $name
        createdByEmail: $createdByEmail
        environment: $environment
        redirectUri: $redirectUri
        syncSettings: {
          autoSync: $autoSync
          syncAccounts: $syncAccounts
          syncLeads: $syncLeads
          exportTechInstalls: $exportTechInstalls
          exportFirmographics: $exportFirmographics
          exportAccountScoring: $exportAccountScoring
          exportCloudDynamics: $exportCloudDynamics
          exportItSpend: $exportItSpend
          useCountryLevelMatching: $useCountryLevelMatching
          accountsCountryField: $accountsCountryField
          localizeInstalls: $localizeInstalls
          customHgIdMatching: $customHgIdMatching
        }
      }
    ) {
      messages {
        code
        field
        message
        options {
          key
          value
        }
        template
      }
      successful
      result {
        id
        name
        organizationId
        environment
        managedPackageCurrentVersion
      }
      __typename
    }
  }
`;

// Mutation to reauthenticate an existing integration
export const REAUTHENTICATE_INTEGRATION_MUTATION = gql`
  mutation reauthenticateIntegration(
    $authorizationCodeGrant: String!
    $redirectUri: String!
    $integrationId: String!
  ) {
    reauthenticateIntegration(
      input: {
        authorizationCodeGrant: $authorizationCodeGrant
        redirectUri: $redirectUri
        integrationId: $integrationId
      }
    )
  }
`;

// Mutation to update integration sync settings
export const UPDATE_INTEGRATION_SYNC_SETTINGS_MUTATION = gql`
  mutation UpdateSfdcIntegrationSyncSettings(
    $integrationId: ID!
    $syncSettings: IntegrationSyncSettingsInput!
  ) {
    updateIntegrationSyncSettings(
      input: { integrationId: $integrationId, syncSettings: $syncSettings }
    ) {
      id
      syncSettings {
        autoSync
        syncAccounts
        syncLeads
        exportItSpend
        exportTechInstalls
        exportFirmographics
        exportAccountScoring
        selectedScoringId
        exportCloudDynamics
        useCountryLevelMatching
        accountsCountryField
        localizeInstalls
        customHgIdMatching
      }
    }
  }
`;

// Mutation to start enrichment synchronization
export const ENRICHMENT_SYNCHRONIZATION_MUTATION = gql`
  mutation enrichmentSynchronization($integrationId: ID!) {
    enrichmentSynchronization(integrationId: $integrationId) {
      state
    }
  }
`;

// Mutation to update selected products
export const UPDATE_SELECTED_PRODUCTS_MUTATION = gql`
  mutation updateSelectedProducts($integrationId: ID!, $ids: [Int!]!) {
    updateSelectedProducts(
      input: { integrationId: $integrationId, products: $ids }
    ) {
      selectedProducts
    }
  }
`;

// Mutation to update selected spend categories
export const UPDATE_SELECTED_SPEND_CATEGORIES_MUTATION = gql`
  mutation updateSelectedSpendCategories(
    $integrationId: ID!
    $ids: [String!]!
  ) {
    updateSelectedSpendCategories(
      input: { integrationId: $integrationId, spendCategories: $ids }
    ) {
      selectedSpendCategories
    }
  }
`;

// Query to get selected products for an integration
export const GET_SELECTED_PRODUCTS_QUERY = gql`
  query getSelectedProducts($integrationId: ID!) {
    selectedProducts(integrationId: $integrationId)
  }
`;

// Query to get selected spend categories for an integration
export const GET_SELECTED_SPEND_CATEGORIES_QUERY = gql`
  query getSelectedSpendCategories($integrationId: ID!) {
    selectedSpendCategories(integrationId: $integrationId)
  }
`;

// Mutation to generate sync errors report
export const GENERATE_SYNC_ERRORS_REPORT_MUTATION = gql`
  mutation generateSyncErrorsReport($integrationId: ID!) {
    generateSyncErrorsReport(integrationId: $integrationId) {
      status
    }
  }
`;

export const RENAME_INTEGRATION_MUTATION = gql`
  mutation renameIntegration($integrationId: ID!, $name: String!) {
    updateIntegrationName(
      input: { integrationId: $integrationId, name: $name }
    ) {
      id
      name
    }
  }
`;

// Mutation to delete an integration
export const DELETE_SFDC_INTEGRATION_MUTATION = gql`
  mutation DeleteSfdcIntegration($id: ID!) {
    deleteIntegration(integrationId: $id)
  }
`;
